import { StorageService } from "@/services/storageService";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAPI } from "../useAPI";
import { buildSignedUrlCacheKey, getOrFetchSignedUrl, invalidateSignedUrl } from "./signedUrlCache";
import type { UseSignedUrlOptions } from "./types";

type ManagedState = {
  /** The cache key this url/error belongs to — lets a stale, still-in-flight fetch for a
   * previous key be ignored by the derived `url`/`error` below without waiting on an effect. */
  key: string | null;
  url: string;
  error: unknown;
};

const INITIAL_MANAGED_STATE: ManagedState = { key: null, url: "", error: undefined };

const PROACTIVE_REFRESH_LEAD_MS = 5 * 60_000; // re-mint 5 minutes before expiry, well inside the TTL

/** Reactive signed URL for `/storage/{fileId}` — fetches on mount, re-mints itself shortly before
 * expiry, and can also be `refresh()`ed on demand. Pass `onInitialError` to handle a mount-time
 * failure without polling `error`. */
export function useSignedUrl(
  fileId?: string | null,
  { type = "file", onInitialError }: UseSignedUrlOptions = {}
): {
  url: string;
  isLoading: boolean;
  error: unknown;
  refresh: () => void;
} {
  const api = useAPI();
  const storageService = useMemo(() => new StorageService(api), [api]);

  const key = fileId ? buildSignedUrlCacheKey(fileId, type) : null;

  const [state, setState] = useState<ManagedState>(INITIAL_MANAGED_STATE);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const onErrorRef = useRef(onInitialError);
  onErrorRef.current = onInitialError;

  // Guards both the mount/key fetch and refresh() fetches: only the fetch whose id still
  // matches when it settles may write state, so a superseded fetch (unmount, key change, or a
  // newer refresh) is silently ignored — no effect-ordering dependency between the two paths.
  const fetchIdRef = useRef(0);
  const proactiveRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runFetch = useCallback(
    (fetchKey: string, fetchFileId: string, reportInitialError: boolean) => {
      const fetchId = ++fetchIdRef.current;
      setIsFetching(true);
      getOrFetchSignedUrl(fetchKey, () => storageService.getSignedUrl(fetchFileId, type))
        .then((resolved) => {
          if (fetchIdRef.current !== fetchId) return;
          setState({ key: fetchKey, url: resolved.signed_url, error: undefined });
          setIsFetching(false);

          if (proactiveRefreshTimeoutRef.current) {
            clearTimeout(proactiveRefreshTimeoutRef.current);
          }
          const expiresAtMs = resolved.expires_at ? Date.parse(resolved.expires_at) : NaN;
          if (Number.isFinite(expiresAtMs)) {
            const delayMs = Math.max(0, expiresAtMs - PROACTIVE_REFRESH_LEAD_MS - Date.now());
            proactiveRefreshTimeoutRef.current = setTimeout(() => {
              if (fetchIdRef.current === fetchId) {
                // Cache freshness (CLOCK_SKEW_BUFFER_MS) is a much shorter window than
                // PROACTIVE_REFRESH_LEAD_MS, so without invalidating first, runFetch would just
                // hand back the same still-"fresh" cached url instead of re-minting.
                invalidateSignedUrl(fetchKey);
                runFetch(fetchKey, fetchFileId, false);
              }
            }, delayMs);
          }
        })
        .catch((error: unknown) => {
          if (fetchIdRef.current !== fetchId) return;
          if (reportInitialError) {
            onErrorRef.current?.(error);
          }
          setState({ key: fetchKey, url: "", error });
          setIsFetching(false);
        });
    },
    [storageService, type]
  );

  useEffect(() => {
    if (!key || !fileId) {
      setState({ key, url: "", error: undefined });
      setIsFetching(false);
      return undefined;
    }

    runFetch(key, fileId, true);

    // Also invalidates a refresh() fetch started under this key, since it bumps the same
    // fetchIdRef — a single guard covers both unmount and key-change for either fetch path.
    return () => {
      fetchIdRef.current += 1;
      if (proactiveRefreshTimeoutRef.current) {
        clearTimeout(proactiveRefreshTimeoutRef.current);
        proactiveRefreshTimeoutRef.current = null;
      }
    };
  }, [key, fileId, runFetch]);

  // `state` still holds the previous key's url/error on the very first render after `key`
  // changes (the fetch effect hasn't run yet) — derive against the current key so neither is
  // ever shown stale, and so `isLoading` flips true immediately too, without waiting on effect
  // timing. `fetchIdRef` above still guards a separate case: a late resolution for an abandoned
  // key/fetch writing into `state` after the fact.
  const isStaleForKey = state.key !== key;
  const url = isStaleForKey ? "" : state.url;
  const error = isStaleForKey ? undefined : state.error;
  const isLoading = Boolean(key) && (isStaleForKey || isFetching);

  const refresh = useCallback(() => {
    if (!key || !fileId) {
      return;
    }
    invalidateSignedUrl(key);
    // The caller only refreshes because the current url is suspected dead (e.g. the viewer
    // failed to load it), so clearing it immediately avoids a window of showing a stale url.
    setState({ key, url: "", error: undefined });
    runFetch(key, fileId, false);
  }, [key, fileId, runFetch]);

  return { url, isLoading, error, refresh };
}
